import { useContext, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { InputLabel as MUIInputLabel } from '@mui/material'
import {
  CREATE_OR_UPDATE_DEMOGRAPHIC_DATA,
  UPDATE_PATIENT,
  UPDATE_PATIENT_CONTACT_INFO,
} from 'domains/carrotGraph/mutations'
import { PatientContext, UserPHIContext } from 'ui/contexts'
import { Stack, Text } from 'ui/baseComponents'
import CopyTextIcon from 'ui/components/CopyText/CopyTextIcon'
import ActionButtons from 'ui/screens/EditPatient/PatientDetails/ActionButtons'
import AlertError from './AlertError'
import AlertSuccess from 'ui/screens/EditPatient/PatientDetails/AlertSuccess'
import FormField from './FormField'
import PatientDetailsWithoutPHI from './PatientDetailsWithoutPHI'
import ProviderSearchField from './ProviderSearchField'
import {
  FORM_FIELD_WIDTH,
  FORM_FIELD_WIDTH_UNITS,
  FORM_FIELDS,
  FORM_FIELDS_METADATA,
  MUTATION,
  PROVIDER_SEARCH_FIELDS,
  TYPOGRAPHY,
} from 'ui/screens/EditPatient/PatientDetails/consts'
import { DATE_FORMAT } from 'ui/consts'
import {
  constructProviderFromPatient,
  getFieldWidth,
  transformDemographicData,
  transformPatientContactInfo,
} from './helpers'
import { validatePhoneNumber } from 'ui/baseComponents/FormFields/phoneInputHelpers'
import {
  usePatientEditableFieldsDropdownOptions,
  useProviderSearch,
} from 'ui/hooks'
import { useRefetchPatientEditableFields } from 'ui/hooks/usePatientEditableFields'
import { isNumericString } from 'utilities/string'
import {
  getNestedObjectValue,
  updateNestedObjectValue,
} from 'utilities/objects'
import { colors } from 'theme/colors'

const STYLES = {
  copyTextLabel: {
    '&.MuiInputLabel-root': {
      marginBottom: '-16px',
      color: colors.GREY[800],
    },
  },
}

const TYPOGRAPHY_COLOR_HEADINGS = colors.PRIMARY[1000]

const PatientDetails = () => {
  const patient = useContext(PatientContext)
  const phiVisibility = useContext(UserPHIContext)
  const [updatePatient] = useMutation(UPDATE_PATIENT, {
    refetchQueries: [useRefetchPatientEditableFields(patient.id)],
  })
  const [updatePatientContactInfo] = useMutation(UPDATE_PATIENT_CONTACT_INFO, {
    refetchQueries: [useRefetchPatientEditableFields(patient.id)],
  })
  const [createOrUpdateDemographicData] = useMutation(
    CREATE_OR_UPDATE_DEMOGRAPHIC_DATA,
    {
      refetchQueries: [useRefetchPatientEditableFields(patient.id)],
    },
  )
  const [errorUpdatingData, setErrorUpdatingData] = useState(false)
  const [successUpdatingData, setSuccessUpdatingData] = useState(false)
  const [errors, setErrors] = useState({})
  const [formDataDemographicData, setFormDataDemographicData] = useState({}) // Only keeps track of the fields that change
  const [formDataPatientContactInfo, setFormDataPatientContactInfo] = useState(
    {},
  ) // Only keeps track of the fields that change
  const [formDataPatientCodeName, setFormDataPatientCodeName] = useState({})
  const [isEditing, setIsEditing] = useState(false)

  const { options, loading: loadingOptions } =
    usePatientEditableFieldsDropdownOptions()

  const handleProviderSelect = (provider) => {
    setFormDataDemographicData((prevFormData) => ({
      ...prevFormData,
      identifiableInfo: {
        ...prevFormData?.identifiableInfo,
        demographicData: {
          ...prevFormData?.identifiableInfo?.demographicData,
          providerAddress: {
            addressLine1: provider?.address?.address_line_1 || '',
            addressLine2: provider?.address?.address_line_2 || '',
            city: provider?.address?.city || '',
            state: provider?.address?.state || '',
            zipCode: provider?.address?.zip_code || '',
          },
          providerInfo: {
            firstName: provider?.first_name || '',
            lastName: provider?.last_name || '',
          },
          providerNpi: provider?.npi || '',
        },
      },
    }))
  }

  const {
    providerLocation,
    providerOptions,
    searchProviderInputFields,
    selectedProvider,
    updateProviderLocation,
    updateSearchProviderInputFields,
    updateSelectedProvider,
  } = useProviderSearch({
    onProviderSelect: handleProviderSelect,
    providerState:
      getNestedObjectValue({
        object: patient,
        path: FORM_FIELDS_METADATA.stateForSearch.name,
      }) || '',
    searchFields: PROVIDER_SEARCH_FIELDS,
  })

  const selectPatientsCurrentProvider = (patient) => {
    const initialProvider = constructProviderFromPatient(patient)
    updateSelectedProvider({
      target: {
        name: 'initialProvider',
        value: initialProvider,
      },
    })
  }

  useEffect(() => {
    if (
      patient?.identifiableInfo?.demographicData?.providerInfo?.firstName ||
      patient?.identifiableInfo?.demographicData?.providerInfo?.lastName
    ) {
      selectPatientsCurrentProvider(patient)
    }
  }, [patient]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateFormData = ({ mutationField, name, value }) => {
    const updateFunctions = {
      [MUTATION.updatePatientContactInfo]: setFormDataPatientContactInfo,
      [MUTATION.createOrUpdateDemographicData]: setFormDataDemographicData,
      [MUTATION.updatePatient]: setFormDataPatientCodeName,
    }

    const updateFunction = updateFunctions[mutationField]

    if (updateFunction) {
      updateFunction((prev) =>
        updateNestedObjectValue({ object: prev, path: name, value }),
      )
    } else {
      console.error('No mutation field found for:', mutationField)
    }
  }

  const handleChange = ({ event }) => {
    const { name, value } = event.target

    if (name === FORM_FIELDS_METADATA.stateForSearch.name) {
      updateProviderLocation(value)
    }

    const fieldIdentifier = Object.keys(FORM_FIELDS_METADATA).find(
      (key) => FORM_FIELDS_METADATA[key].name === name,
    )

    const mutationField = FORM_FIELDS_METADATA[fieldIdentifier].mutation

    updateFormData({ mutationField, name, value })
  }

  const handleDateChange = (event) => {
    const { name, value } = event.target
    const newDate = value ? value.format(DATE_FORMAT) : null

    setFormDataDemographicData((prev) =>
      updateNestedObjectValue({ object: prev, path: name, value: newDate }),
    )
  }

  const handleMultiSelectChange = (event) => {
    const { name, value } = event.target

    let currentOptions = []
    if (name === FORM_FIELDS_METADATA.geneticMarkers.name) {
      currentOptions = options.geneticMarkers
    } else if (name === FORM_FIELDS_METADATA.otherConditions.name) {
      currentOptions = options.diagnoses
    }

    const newValue = value
      .map((optionId) =>
        currentOptions.find((option) => option.id === optionId),
      )
      .filter((option) => option !== undefined) // eslint-disable-line no-undefined
      .sort((a, b) => a.name.localeCompare(b.name))

    setFormDataDemographicData((prev) =>
      updateNestedObjectValue({ object: prev, path: name, value: newValue }),
    )
  }

  const validateFields = () => {
    const newErrors = {}

    Object.keys(FORM_FIELDS_METADATA).forEach((key) => {
      const { name, validate } = FORM_FIELDS_METADATA[key]

      if (validate) {
        const isContactInfoField = getNestedObjectValue({
          object: formDataPatientContactInfo,
          path: name,
        })

        const formData = isContactInfoField
          ? formDataPatientContactInfo
          : formDataDemographicData

        const value = getNestedObjectValue({ object: formData, path: name })
        const error = validate({
          formData,
          getNestedObjectValue,
          isNumericString,
          patient,
          value,
          validatePhoneNumber,
        })

        if (error) {
          newErrors[name] = error
        }
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const onClickEdit = () => setIsEditing(true)

  const resetForm = () => {
    setFormDataDemographicData({})
    setFormDataPatientCodeName({})
    setFormDataPatientContactInfo({})
    setIsEditing(false)
    setErrorUpdatingData(false)
    setErrors({})
  }

  const onClickSave = async () => {
    const codeName = formDataPatientCodeName?.codeName
    const contactInfo = formDataPatientContactInfo?.identifiableInfo
    const demographicData =
      formDataDemographicData?.identifiableInfo?.demographicData

    const isInputValid = validateFields()

    if (!isInputValid) return

    const handleMutation = async ({ mutation, variables, errorHandler }) => {
      try {
        await mutation({ variables })
      } catch (error) {
        errorHandler()
        return false
      }
      return true
    }

    const handleError = () => setErrorUpdatingData(true)

    if (codeName) {
      const success = await handleMutation({
        mutation: updatePatient,
        variables: {
          input: {
            patientId: patient.id,
            codeName,
          },
        },
        errorHandler: handleError,
      })
      if (!success) return
    }

    if (contactInfo) {
      const transformedContactInfo = transformPatientContactInfo(contactInfo)

      const success = await handleMutation({
        mutation: updatePatientContactInfo,
        variables: {
          input: {
            patientId: patient.id,
            ...transformedContactInfo,
          },
        },
        errorHandler: handleError,
      })
      if (!success) return
    }

    if (demographicData) {
      const transformedDemographicData =
        transformDemographicData(demographicData)

      const success = await handleMutation({
        mutation: createOrUpdateDemographicData,
        variables: {
          input: {
            patientId: patient.id,
            ...transformedDemographicData,
          },
        },
        errorHandler: handleError,
      })
      if (!success) return
    }

    // Don't display the success message if no data was updated and the user clicks Save
    if (codeName || contactInfo || demographicData) {
      setSuccessUpdatingData(true)
    }
    resetForm()
  }

  const onClickCancel = () => {
    resetForm()
    selectPatientsCurrentProvider(patient)
  }

  const renderFields = (fields) => {
    const rows = []
    let currentRow = []
    let currentRowWidth = 0

    fields.forEach((field, index) => {
      const { layout } = field
      const fieldWidth = getFieldWidth({
        layout,
        unit: FORM_FIELD_WIDTH_UNITS.numeric,
      })

      if (currentRowWidth + fieldWidth > 1) {
        rows.push(currentRow)
        currentRow = []
        currentRowWidth = 0
      }

      currentRow.push(field)
      currentRowWidth += fieldWidth

      if (
        field.layout === FORM_FIELD_WIDTH.full.name ||
        field.isOnlyFieldInRow ||
        index === fields.length - 1
      ) {
        rows.push(currentRow)
        currentRow = []
        currentRowWidth = 0
      }
    })

    return rows.map((row, rowIndex) => (
      <Stack key={rowIndex} direction="row" spacing={2}>
        {row.map((field) => (
          <FormField
            {...{
              errors,
              field,
              formDataDemographicData,
              formDataPatientCodeName,
              formDataPatientContactInfo,
              handleChange,
              handleDateChange,
              handleMultiSelectChange,
              isEditing,
              key: field.name,
              loadingOptions,
              options,
              patient,
            }}
          />
        ))}
      </Stack>
    ))
  }

  if (!phiVisibility) {
    return <PatientDetailsWithoutPHI />
  }

  return (
    <Stack spacing={2} sx={{ width: '100%', padding: 2 }}>
      {errorUpdatingData && (
        <AlertError onClose={() => setErrorUpdatingData(false)} />
      )}
      {successUpdatingData && (
        <AlertSuccess onClose={() => setSuccessUpdatingData(false)} />
      )}
      <ActionButtons
        {...{
          isEditing,
          onClickEdit,
          onClickSave,
          onClickCancel,
        }}
      />
      <Stack direction="row" gap={8}>
        <Stack direction="column" spacing={2} width="50%">
          <Text
            color={TYPOGRAPHY_COLOR_HEADINGS}
            variant={TYPOGRAPHY.sectionHeading}
          >
            Personal Information
          </Text>
          <Stack gap={0}>
            <MUIInputLabel shrink={true} sx={STYLES.copyTextLabel}>
              Patient ID
            </MUIInputLabel>
            <CopyTextIcon value={patient.id} width="100%" />
          </Stack>
          {renderFields(FORM_FIELDS.personalInfo)}
          <Text
            color={TYPOGRAPHY_COLOR_HEADINGS}
            variant={TYPOGRAPHY.sectionSubHeading}
          >
            Health Information
          </Text>
          {renderFields(FORM_FIELDS.healthInfo)}
          <Text
            color={TYPOGRAPHY_COLOR_HEADINGS}
            variant={TYPOGRAPHY.sectionSubHeading}
          >
            Demographic Information
          </Text>
          {renderFields(FORM_FIELDS.demographicInfo)}
          <Text
            color={TYPOGRAPHY_COLOR_HEADINGS}
            variant={TYPOGRAPHY.sectionSubHeading}
          >
            Code Name
          </Text>
          {renderFields(FORM_FIELDS.codeName)}
        </Stack>
        <Stack direction="column" spacing={2} width="50%">
          <Text
            color={TYPOGRAPHY_COLOR_HEADINGS}
            variant={TYPOGRAPHY.sectionHeading}
          >
            Clinical Information
          </Text>
          <Text variant={TYPOGRAPHY.instructions}>
            If the exact next visit date is known, please enter a value in
            Clinical Visit. If only the visit month is known, please enter a
            value in Next Visit Month. Do not populate both fields.
          </Text>
          {renderFields(FORM_FIELDS.clinicalInfoFields)}
          <ProviderSearchField
            {...{
              disabled: !isEditing,
              errors,
              formDataDemographicData,
              handleChange,
              inputValues: searchProviderInputFields,
              isEditing,
              loadingOptions,
              options,
              patient,
              providerLocation,
              providerOptions,
              updateSearchProviderInputFields,
              updateSelectedProvider,
              value: selectedProvider,
            }}
          />
          {renderFields(FORM_FIELDS.facilityInfo)}
        </Stack>
      </Stack>
      <ActionButtons
        {...{
          isEditing,
          onClickEdit,
          onClickSave,
          onClickCancel,
        }}
      />
      {errorUpdatingData && (
        <AlertError onClose={() => setErrorUpdatingData(false)} />
      )}
      {successUpdatingData && (
        <AlertSuccess onClose={() => setSuccessUpdatingData(false)} />
      )}
    </Stack>
  )
}

export default PatientDetails
